/* Live multilingual product-name search. No private API or frozen catalogue. */
let cataloguePromise;
const config = JSON.parse(document.getElementById('mg-bilingual-search-config').textContent);
const locale = config.locale.toLowerCase();
const words = locale.startsWith('zh')
  ? locale.includes('hant') || locale.includes('tw')
    ? { products: '商品', loading: '搜尋中…', empty: '找不到相符商品，請試用其他名稱。', error: '暫時無法搜尋，請稍後再試。', count: n => `找到 ${n} 件商品` }
    : { products: '商品', loading: '搜索中…', empty: '找不到相符商品，请试用其他名称。', error: '暂时无法搜索，请稍后再试。', count: n => `找到 ${n} 件商品` }
  : { products: 'Products', loading: 'Searching…', empty: 'No matching products. Try another name.', error: 'Search is temporarily unavailable. Please try again.', count: n => `${n} product${n === 1 ? '' : 's'} found` };

export function normalize(value) {
  return String(value || '').normalize('NFKC').toLowerCase().replace(/[’']/g, '').replace(/[^\p{L}\p{N}]+/gu, ' ').trim();
}

export function matchProducts(products, query) {
  const phrase = normalize(query);
  if (!phrase) return [];
  const terms = phrase.split(/\s+/);
  return products.map(product => {
    const titles = product.names.map(normalize);
    const hay = normalize([product.handle, ...product.names, ...product.types, ...product.tags, ...product.sku].join(' '));
    if (!terms.every(term => hay.includes(term))) return null;
    const score = titles.some(title => title === phrase) ? 0 : titles.some(title => title.startsWith(phrase)) ? 1 : titles.some(title => title.includes(phrase)) ? 2 : 3;
    return { product, score };
  }).filter(Boolean).sort((a,b) => a.score-b.score || a.product.title.localeCompare(b.product.title)).map(row => row.product);
}

async function loadLanguage(language) {
  const products = [];
  let page = 1;
  while (page) {
    const url = new URL(`${language.root.replace(/\/$/, '')}/collections/all`, location.origin);
    url.searchParams.set('section_id', 'mg-search-index');
    url.searchParams.set('page', page);
    const preview = new URL(location.href).searchParams.get('preview_theme_id');
    if (preview) url.searchParams.set('preview_theme_id', preview);
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15000);
    let response, html;
    try {
      response = await fetch(url, { signal: controller.signal, credentials: 'same-origin' });
      if (!response.ok) throw new Error(`Catalogue response ${response.status}`);
      html = await response.text();
    } finally { clearTimeout(timeout); }
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const index = parsed.querySelector('[data-mg-search-index]');
    const data = index?.querySelector('[data-mg-search-products]');
    if (!data) throw new Error('Catalogue response missing');
    products.push(...JSON.parse(data.textContent));
    const next = Number(index.dataset.nextPage) || 0;
    if (next && next <= page) throw new Error('Invalid catalogue pagination');
    page = next;
  }
  return { language, products };
}

async function loadCatalogue() {
  const languages = config.languages.filter(language => language.code === 'en' || language.code.startsWith('zh') || language.code === config.locale);
  const rows = await Promise.all(languages.map(loadLanguage));
  const current = rows.find(row => row.language.code === config.locale);
  if (!current) throw new Error('Current storefront language missing');
  // A translated match can only return a product visible in the current market.
  const products = new Map(current.products.map(product => [product.id, { ...product, names: [], types: [], tags: [], sku: [] }]));
  for (const row of rows) for (const item of row.products) {
    const product = products.get(item.id);
    if (!product) continue;
    product.names.push(item.title);
    product.types.push(item.type);
    product.tags.push(...item.tags);
    product.sku.push(...item.sku);
  }
  return [...products.values()];
}

export async function findProducts(query) {
  if (!cataloguePromise) cataloguePromise = loadCatalogue().catch(error => { cataloguePromise = null; throw error; });
  return matchProducts(await cataloguePromise, query);
}

function element(tag, className, text) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

export function resultMarkup(products, query, state = 'ready', count = products.length) {
  const root = element('div', 'predictive-search-dropdown');
  root.id = 'predictive-search-results';
  root.dataset.mgQuery = query;
  root.setAttribute('role', 'listbox');
  root.setAttribute('aria-label', words.products);
  root.setAttribute('aria-expanded', 'true');
  const inner = element('div', 'predictive-search-results__inner');
  inner.dataset.searchResults = '';
  const status = element('div', products.length ? 'visually-hidden' : 'predictive-search-results__no-results', state === 'loading' ? words.loading : state === 'error' ? words.error : count ? words.count(count) : words.empty);
  status.setAttribute('role', 'status');
  inner.append(status);
  if (products.length) {
    const group = element('div', 'predictive-search-results__products');
    group.append(element('h4', 'predictive-search-results__title', words.products));
    const list = element('ul', 'predictive-search-results__list predictive-search-results__wrapper-products list-unstyled');
    for (const product of products) {
      const item = element('li', 'predictive-search-results__card predictive-search-results__card--product');
      item.setAttribute('ref', 'resultsItems[]');
      const link = element('a', 'resource-card');
      link.href = product.url;
      link.setAttribute('aria-label', product.title);
      link.style.cssText = 'display:flex;flex-direction:column;text-decoration:none;color:inherit;min-width:0';
      if (product.image) {
        const image = element('img', 'resource-card__image');
        image.src = product.image; image.alt = product.title; image.loading = 'lazy';
        image.width = 352; image.height = 352;
        image.style.cssText = 'width:100%;height:auto;aspect-ratio:1;object-fit:contain;background:#ededed';
        link.append(image);
      }
      const content = element('div', 'resource-card__content');
      content.append(element('p', 'resource-card__title paragraph', product.title));
      const price = element('span', 'mg-money', product.price);
      price.dataset.hkd = product.amount; price.dataset.orig = product.price;
      content.append(price); link.append(content); item.append(link); list.append(item);
    }
    group.append(list); inner.append(group);
  }
  root.append(inner);
  return root;
}

export async function goToSearch(query, form) {
  query = query.trim();
  if (form?.dataset.mgSubmitting) return;
  if (form) form.dataset.mgSubmitting = 'true';
  try {
    const url = new URL(config.search, location.origin);
    if (query) {
      const products = await findProducts(query);
      url.searchParams.set('q', products.length ? products.map(product => `id:${product.id}`).join(' OR ') : 'id:0');
      url.searchParams.set('mg_query', query);
      url.searchParams.set('type', 'product');
    }
    const preview = new URL(location.href).searchParams.get('preview_theme_id');
    if (preview) url.searchParams.set('preview_theme_id', preview);
    location.assign(url.toString());
  } catch (error) {
    if (form) {
      let status = form.querySelector('[data-mg-search-error]');
      if (!status) { status = element('p', '', words.error); status.dataset.mgSearchError = ''; status.setAttribute('role','alert'); form.append(status); }
    }
    console.error('Bilingual product search unavailable', error);
  } finally { if (form) delete form.dataset.mgSubmitting; }
}

document.addEventListener('submit', event => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.matches('.predictive-search-form,.search-page-input__parent')) return;
  const input = form.querySelector('input[name="q"]');
  if (!input) return;
  event.preventDefault();
  void goToSearch(input.value, form);
}, true);

function syncSearchPage() {
  if (!location.pathname.replace(/\/$/,'').endsWith('/search')) return;
  const url = new URL(location.href);
  const query = url.searchParams.get('mg_query');
  const original = url.searchParams.get('q') || '';
  if (query === null) {
    if (original) void goToSearch(original, document.querySelector('.search-page-input__parent'));
    return;
  }
  const input = document.querySelector('.search-page-input');
  if (input) input.value = query;
  const header = document.querySelector('.search-page__header');
  if (header && original) {
    const walk = document.createTreeWalker(header, NodeFilter.SHOW_TEXT);
    while (walk.nextNode()) walk.currentNode.textContent = walk.currentNode.textContent.replaceAll(original, query);
  }
  document.title = document.title.replace(original, query);
  if (original === 'id:0') {
    const results = document.querySelector('results-list');
    if (results) results.hidden = true;
  }
}
syncSearchPage();
